import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { mergeStoreLocations } from "../../utils/mergeStoreLocations";
import type { GridData } from "../../utils/summary/editPropagation";
import { markdownFlowSign } from "../../utils/summary/salesKpiDerivations";
import type { ForecastSummaryData } from "../useFetchForecastSummaryData";
import { useFetchForecastSummaryData } from "../useFetchForecastSummaryData";
import { useSaveForecastSummaryData } from "../useSaveForecastSummaryData";
import { isBudgetCubeFrozen } from "./useSummarySourceSelection";

/** Snapshot of the filters that were last applied. */
export interface AppliedFilters {
  /** Context filter selections at the time of apply. */
  context: ForecastContextSelectedOptions;
  /** Selected producer type. */
  producer: string;
  /** Selected cube version ID. */
  version: string;
  /** Selected financial year. */
  year: string;
  /** Dimension used for table grouping. */
  tableDim: string;
  /** Dimension used for row grouping. */
  rowDim: string;
  /** Producer of the index (baseline) source. */
  idxProducer: string;
  /** Cube version ID of the index source ("" when no index requested). */
  idxVersion: string;
  /** Financial year of the index source ("" when no index requested). */
  idxYear: string;
  /** Whether the loaded cube was frozen at apply. */
  isFrozen: boolean;
}

/** Builds the table_data payload for the save endpoint from filtered grid data. */
function buildSavePayload(
  tableLabels: string[],
  filteredData: Record<string, (number | null)[][]>,
  filteredRowLabelsPerTable: string[][],
  appliedRowDim: string
): Record<string, Record<string, unknown>[]> {
  const tableData: Record<string, Record<string, unknown>[]> = {};
  for (let ti = 0; ti < tableLabels.length; ti++) {
    const label = tableLabels[ti];
    if (label === "TTL") continue;
    const rows: Record<string, unknown>[] = [];
    const rowLabels = filteredRowLabelsPerTable[ti] ?? [];
    for (let ri = 0; ri < rowLabels.length; ri++) {
      if (rowLabels[ri] === "TTL") continue;
      const row: Record<string, unknown> = { [appliedRowDim]: rowLabels[ri] };
      for (const [col, tables] of Object.entries(filteredData)) {
        if (col.endsWith("|index") || col.endsWith("|TTL")) continue;
        row[col] = tables[ti]?.[ri] ?? null;
      }
      rows.push(row);
    }
    tableData[label] = rows;
  }
  return tableData;
}

/**
 * Whether the guard's Save & apply may write the loaded grid: its cube is in
 * the page's write scope and not frozen. Reads the applied cube, never the
 * picker — live while the budget list offers it, else the Apply snapshot.
 */
function canSaveLoadedGrid(
  appliedFilters: AppliedFilters | null,
  source: UseSummaryHandlersOptions["source"]
): boolean {
  if (appliedFilters?.producer !== source.saveProducer) return false;
  if (appliedFilters.producer !== "budget") return true;
  return !(
    isBudgetCubeFrozen(source.budgetVersionsData, appliedFilters.version) ??
    appliedFilters.isFrozen
  );
}

/** The inputs Apply requires, in screen order. */
type RequiredSource = Pick<
  UseSummaryHandlersOptions["source"],
  "tableDim" | "rowDim" | "producer" | "version" | "year"
>;

/**
 * The empty fields a blocked Apply flags. Version and year stay disabled until
 * the one before them is picked, so an empty one behind an empty predecessor
 * is not flagged.
 */
function missingFields(required: RequiredSource) {
  return (Object.keys(required) as (keyof RequiredSource)[]).filter(
    (key) =>
      !required[key] &&
      !(key === "version" && !required.producer) &&
      !(key === "year" && !required.version)
  );
}

/** Options for the summary handlers sub-hook. */
interface UseSummaryHandlersOptions {
  /** Current (unapplied) context selections. */
  selectedContextOptions: ForecastContextSelectedOptions;
  /** Resolved store locations for context merging. */
  resolvedStoreLocations: string[];
  /** Source selection values. */
  source: {
    producer: string;
    version: string;
    year: string;
    tableDim: string;
    rowDim: string;
    idxProducer: string;
    idxVersion: string;
    idxYear: string;
    isFrozen: boolean;
    saveProducer: string;
    budgetVersionsData: Parameters<typeof isBudgetCubeFrozen>[0];
  };
  /** Grid state for save. */
  grid: {
    gridData: GridData | null;
    tableLabels: string[];
    filteredData: Record<string, (number | null)[][]>;
    filteredRowLabelsPerTable: string[][];
    applyResponse: (
      response: ForecastSummaryData,
      markdownSign?: number
    ) => void;
    setSavedAt: (v: string | null) => void;
    hasUnsavedEdits: boolean;
    setHasUnsavedEdits: (v: boolean) => void;
  };
  /** Applied filters state. */
  appliedFilters: AppliedFilters | null;
  /** Setter for applied filters. */
  setAppliedFilters: Dispatch<SetStateAction<AppliedFilters | null>>;
}

/** The sources Apply still needs, and whether a blocked Apply flags them. */
function useSourceErrors(source: RequiredSource) {
  const { tableDim, rowDim, producer, version, year } = source;
  const required = { tableDim, rowDim, producer, version, year };
  const missingSources = missingFields(required);

  // A blocked Apply flags the sources until any input changes.
  const inputs = Object.values(required).join("|");
  const [hasSourceError, setHasSourceError] = useState(false);
  const [flaggedInputs, setFlaggedInputs] = useState(inputs);
  if (flaggedInputs !== inputs) {
    setFlaggedInputs(inputs);
    setHasSourceError(false);
  }
  return {
    missingSources,
    canApply: missingSources.length === 0,
    hasSourceError,
    setHasSourceError
  };
}

/**
 * Encapsulates the apply and save handlers for the summary page.
 */
export function useSummaryHandlers(options: UseSummaryHandlersOptions) {
  const {
    selectedContextOptions,
    resolvedStoreLocations,
    source,
    grid,
    appliedFilters,
    setAppliedFilters
  } = options;

  const { isFetching, fetch: fetchSummary } = useFetchForecastSummaryData();
  const { mutateAsync: saveSummaryData, isPending: isSaving } =
    useSaveForecastSummaryData();

  const { missingSources, canApply, hasSourceError, setHasSourceError } =
    useSourceErrors(source);

  /** Loads the selection; resolves whether the data landed. */
  const handleApply = useCallback(async () => {
    if (!canApply) {
      setHasSourceError(true);
      return false;
    }
    const context = mergeStoreLocations(
      selectedContextOptions ?? {},
      resolvedStoreLocations
    );
    try {
      const result = await fetchSummary({
        context,
        cubeId: source.version,
        year: source.year,
        tableDim: source.tableDim,
        rowDim: source.rowDim,
        indexCubeId: source.idxVersion || undefined,
        indexYear: source.idxYear || undefined
      });
      grid.applyResponse(result, markdownFlowSign(context.markdown_status));
      setAppliedFilters({
        context,
        producer: source.producer,
        version: source.version,
        year: source.year,
        tableDim: source.tableDim,
        rowDim: source.rowDim,
        idxProducer: source.idxProducer,
        idxVersion: source.idxVersion,
        idxYear: source.idxYear,
        isFrozen: source.isFrozen
      });
      grid.setSavedAt(null);
      return true;
    } catch {
      // Toast handled by hook
      return false;
    }
  }, [
    selectedContextOptions,
    resolvedStoreLocations,
    source,
    canApply,
    setHasSourceError,
    fetchSummary,
    grid,
    setAppliedFilters
  ]);

  const handleSave = useCallback(async () => {
    if (!appliedFilters || !grid.gridData) {
      throw new Error("Nothing to save: no summary loaded.");
    }
    const tableData = buildSavePayload(
      grid.tableLabels,
      grid.filteredData,
      grid.filteredRowLabelsPerTable,
      appliedFilters.rowDim
    );
    // Cleared before the await so an edit made while the save is in flight
    // stays unsaved.
    grid.setHasUnsavedEdits(false);
    let result: Awaited<ReturnType<typeof saveSummaryData>>;
    try {
      result = await saveSummaryData({
        context: appliedFilters.context ?? {},
        cube_id: appliedFilters.version,
        year: appliedFilters.year,
        row_dim: appliedFilters.rowDim,
        table_dim: appliedFilters.tableDim,
        table_data: tableData
      });
    } catch (error) {
      if (grid.hasUnsavedEdits) grid.setHasUnsavedEdits(true);
      throw error;
    }
    if (result.saved_at) grid.setSavedAt(result.saved_at);
  }, [appliedFilters, grid, saveSummaryData]);

  return {
    isFetching,
    isSaving,
    missingSources,
    hasSourceError,
    handleApply,
    handleSave,
    canSaveFirst: canSaveLoadedGrid(appliedFilters, source)
  };
}
